/**
 * Days as NSB counts them: in its own time zone, Eastern, the same day the API
 * counts in (api `domain/days.ts`). A form's "today" is this, so an entry made
 * after 8 pm is never dated tomorrow and a phone abroad dates it as the office does.
 *
 * Every date the portal shows is written here, one way (B43: "Oct 6" everywhere, never 10/6/2026): a calendar day
 * (YYYY-MM-DD) is that day, never moved by a time zone; a moment (an instant from the API) is read on NSB's clock, so
 * a comment made at 9 pm in New York is dated that day, not tomorrow as UTC has it.
 */
const ZONE = 'America/New_York';

/** Today, as YYYY-MM-DD. */
export const easternToday = (): string => new Date().toLocaleDateString('en-CA', { timeZone: ZONE });

/** The day a moment fell on, Eastern, as YYYY-MM-DD: whether something happened today. */
export const easternDayOf = (at: string): string => new Date(at).toLocaleDateString('en-CA', { timeZone: ZONE });

/** The ways a date reads, each with its own use. */
const FORMS = {
  /** "Oct 6": a tile, a list, this year's things. */
  day: { month: 'short', day: 'numeric' },
  /** "Oct 6, 2026": a record that may be from another year. */
  date: { month: 'short', day: 'numeric', year: 'numeric' },
  /** "October 6, 2026": a sentence, an article. */
  long: { month: 'long', day: 'numeric', year: 'numeric' },
  /** "October 2026": a heading over a month. */
  month: { month: 'long', year: 'numeric' },
  /** "Oct 2026": a chart's axis where a year turns. */
  monthYear: { month: 'short', year: 'numeric' },
  /** "Oct": a chart's axis within a year. */
  monthOnly: { month: 'short' },
  /** "2010": a chart's axis over years. */
  year: { year: 'numeric' },
  /** "Oct 6, 3:04 PM": a comment or a mark. */
  time: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  /** "Oct 6, 2026, 3:04 PM": a message or a key, kept for years. */
  moment: { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

/**
 * "dayOrDate": a step's day on a tile (L-25a; Jason, 2026-10-08, yes to "the year whenever it is not this year"; the
 * plan: "a tile's day carries its year on a job that runs over more than one year"): "Oct 6" in the year it is read in,
 * "Nov 30, 2008" in any other, and with its year wherever the days read together run over more than one calendar year
 * (`years`), so a long job's tiles say which year each was.
 */
export type DateForm = keyof typeof FORMS | 'dayOrDate';

/** How a day on a tile reads its year (`dayOrDate`): the reader's today (the API's, else NSB's), and whether the days read with it run over years. */
export interface YearRule {
  /** YYYY-MM-DD, as the API counts it; NSB's today when not given. */
  today?: string;
  /** The days read together run over more than one calendar year (`yearRule`): every one reads with its year. */
  years?: boolean;
}

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A day or a moment in words (FORMS); blank when there is none (guide §20.3: blank, never a made-up date). */
export function dateWords(at: string | null | undefined, form: DateForm = 'day', rule: YearRule = {}): string {
  if (!at) return '';
  const calendar = CALENDAR_DAY.test(at);
  const d = new Date(calendar ? `${at}T12:00:00Z` : at);
  if (Number.isNaN(d.getTime())) return '';
  const zone = calendar ? 'UTC' : ZONE;
  const shape = form === 'dayOrDate' ? (rule.years || d.toLocaleString('en-US', { year: 'numeric', timeZone: zone }) !== (rule.today ?? easternToday()).slice(0, 4) ? 'date' : 'day') : form;
  return d.toLocaleString('en-US', { ...FORMS[shape], timeZone: zone });
}

/**
 * How a job's days read their year on its tiles (L-25a): on the day it is read (`today`, the API's), each with its year
 * where the job runs over more than one calendar year, from the days its steps were done (calendar days) to that day.
 * NSB's tiles and the client's read the same facts, so the two drawings of a job write its days alike.
 */
export function yearRule(today: string, doneOn: readonly (string | null | undefined)[]): YearRule {
  const years = new Set([today, ...doneOn].filter((d): d is string => Boolean(d)).map((d) => d.slice(0, 4)));
  return { today, years: years.size > 1 };
}
